import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { Group, MapPin, Send, Camera } from "iconoir-react";

interface GroupData {
  id: string;
  name: string;
  description: string;
  rules: string;
  location: string;
  avatar?: string;
  coverImage?: string;
  memberCount: number;
}

const MyGroupDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const group = location.state?.group as GroupData;
  const [comment, setComment] = useState("");
  const [showSuccessNotification] = useState(true);

  if (!group) {
    navigate("/my_group");
    return null;
  }

  const handleSendComment = () => {
    if (comment.trim()) {
      // Handle comment submission
      setComment("");
    }
  };

  return (
    <AuthenticatedLayout backgroundColor="#FBF6F1">
      <div className="w-full h-full bg-[#FBF6F1] flex flex-col relative">
        {/* Status Bar */}
        <div className="w-full h-14 relative">
          <div className="w-32 h-14 left-0 top-0 absolute">
            <div className="left-[49.55px] top-[18.34px] absolute text-center justify-start text-black text-base font-['SF_Pro'] leading-snug">
              9:41
            </div>
          </div>
          <div className="w-32 h-14 left-[240.94px] top-0 absolute">
            <div className="w-6 h-3 left-[78px] top-[23px] absolute opacity-30 rounded-sm border border-black" />
            <div className="w-[1.33px] h-1 left-[104px] top-[27.78px] absolute opacity-40 bg-black" />
            <div className="w-5 h-2 left-[80px] top-[25px] absolute bg-black rounded-xs" />
            <div className="w-4 h-3 left-[53.70px] top-[23.64px] absolute bg-black" />
            <div className="w-5 h-3 left-[27px] top-[23.54px] absolute bg-black" />
          </div>
        </div>

        {/* Success Notification */}
        {showSuccessNotification && (
          <div className="absolute top-[53px] left-[81px] z-10 px-4 py-2 bg-[#E06448] rounded-full inline-flex justify-start items-center gap-2">
            <div className="size-5 relative overflow-hidden">
              <div className="size-4 left-[1.64px] top-[1.64px] absolute outline outline-[1.20px] outline-offset-[-0.60px] outline-[#F4E7DA]" />
              <div className="w-[5px] h-[3.33px] left-[7.50px] top-[8.33px] absolute outline outline-[1.20px] outline-offset-[-0.60px] outline-[#F4E7DA]" />
            </div>
            <div className="justify-start text-[#F4E7DA] text-xs font-normal font-sora leading-none">
              Tạo hội nhóm thành công
            </div>
          </div>
        )}

        {/* Group Header */}
        <div className="w-full px-4 py-2 flex flex-col justify-start items-start gap-2.5">
          <div className="self-stretch h-80 relative bg-[#FBF6F1] rounded-md shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)]">
            <div className="w-full h-36 left-0 top-0 absolute rounded-md">
              <img
                className="w-full h-36 left-0 top-0 absolute rounded-tl-md rounded-tr-md object-cover"
                src={group.coverImage || "https://placehold.co/343x152"}
                alt="Group cover"
              />
              <div className="size- p-3 right-2 top-2 absolute bg-[#F4E7DA] rounded-full shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] inline-flex justify-start items-center gap-2.5">
                <div className="size-6 relative overflow-hidden">
                  <div className="size-2 left-[8.25px] top-[8.25px] absolute " />
                  <div className="size-5 left-[1.25px] top-[1.25px] absolute " />
                </div>
              </div>
            </div>
            <div className="w-full py-4 left-3 top-[82px] absolute inline-flex justify-start items-center gap-9">
              <div className="size- inline-flex flex-col justify-start items-start gap-2">
                <div className="size-24 relative">
                  <img
                    className="size-24 left-0 top-0 absolute rounded-full outline outline-[3px] outline-[#FBF6F1] object-cover"
                    src={group.avatar || "https://placehold.co/100x100"}
                    alt="Group avatar"
                  />
                </div>
                <div className="size- flex flex-col justify-start items-start gap-1.5">
                  <div className="justify-start text-[#1E3A3A] text-2xl font-semibold font-manrope leading-7">
                    {group.name}
                  </div>
                  <div className="size- flex flex-col justify-start items-start gap-1.5">
                    <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                      {group.description}
                    </div>
                    <div className="size- flex flex-col justify-start items-start gap-2">
                      <div className="size- inline-flex justify-start items-center gap-1">
                        <div className="size-4 relative overflow-hidden">
                          <Group className="w-4 h-4 text-[#6F6F6B]" />
                        </div>
                        <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                          {group.memberCount} thành viên
                        </div>
                      </div>
                      <div className="size- inline-flex justify-start items-center gap-1">
                        <div className="size-3.5 relative overflow-hidden">
                          <MapPin className="w-3.5 h-3.5 text-[#6F6F6B]" />
                        </div>
                        <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                          {group.location}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="w-full px-4 py-2 flex flex-col justify-start items-start gap-2.5">
          <div className="self-stretch p-4 bg-[#FBF6F1] rounded-md shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] flex flex-col justify-start items-start gap-4">
            <div className="w-64 flex flex-col justify-start items-start gap-2">
              <div className="self-stretch justify-start text-[#1E3A3A] text-base font-semibold font-manrope leading-tight">
                Bình luận
              </div>
            </div>
            <div className="self-stretch text-center justify-start text-[#6F6F6B] text-sm font-normal font-sora leading-tight">
              Hiện chưa có bình luận nào
            </div>
            <div className="self-stretch bg-[#FBF6F1] inline-flex justify-start items-center gap-2">
              <div className="flex-1 p-3 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] flex justify-between items-center">
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Viết bình luận..."
                  className="flex-1 text-[#6F6F6B] text-sm font-normal font-sora leading-tight bg-transparent outline-none"
                />
                <div
                  className="size-6 relative overflow-hidden cursor-pointer"
                  onClick={handleSendComment}
                >
                  <Send className="w-5 h-5 text-[#6F6F6B]" />
                </div>
              </div>
              <div className="size-6 relative overflow-hidden cursor-pointer">
                <Camera className="w-5 h-5 text-[#6F6F6B]" />
              </div>
            </div>
          </div>
        </div>

        {/* Join Button */}
        <div className="w-full p-4 inline-flex justify-start items-start gap-2.5">
          <div className="flex-1 px-4 py-3.5 bg-[#E06448] rounded-full flex justify-center items-center gap-2.5 cursor-pointer">
            <div className="text-center justify-start text-[#F4E7DA] text-base font-semibold font-manrope leading-snug">
              Tham gia
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default MyGroupDetailPage;
